/**
 * Positioning utilities for intelligent dropdown placement
 *
 * ARCHITECTURE COMPLIANCE:
 * - Pure utility functions - no UI imports
 * - No state management - only calculations
 * - Reusable positioning logic
 */

export interface PositionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportBounds {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

export interface PositionResult {
  x: number;
  y: number;
  placement: {
    horizontal: "left" | "right";
    vertical: "above" | "below";
  };
}

export interface PositionOptions {
  cursorPosition: { x: number; y: number };
  elementSize: { width: number; height: number };
  preferredPlacement?: {
    horizontal?: "left" | "right";
    vertical?: "above" | "below";
  };
  offset?: { x: number; y: number };
  padding?: number; // Minimum distance from viewport edges
}

/**
 * Calculate intelligent positioning for a dropdown element
 * Measures available space and positions to avoid going off-screen
 */
export function calculateIntelligentPosition(
  options: PositionOptions,
): PositionResult {
  const {
    cursorPosition,
    elementSize,
    preferredPlacement = { horizontal: "right", vertical: "below" },
    offset = { x: 0, y: 4 },
    padding = 8,
  } = options;

  // Get viewport bounds
  const viewport: ViewportBounds = {
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
  };

  // Calculate available space in each direction
  const spaceRight = viewport.width - cursorPosition.x - padding;
  const spaceLeft = cursorPosition.x - padding;
  const spaceBelow = viewport.height - cursorPosition.y - padding;
  const spaceAbove = cursorPosition.y - padding;

  // Determine horizontal placement
  let horizontalPlacement: "left" | "right";
  if (preferredPlacement.horizontal === "left") {
    // Prefer left, but check if there's enough space
    horizontalPlacement = spaceLeft >= elementSize.width ? "left" : "right";
  } else {
    // Prefer right, but check if there's enough space
    horizontalPlacement = spaceRight >= elementSize.width ? "right" : "left";
  }

  // Determine vertical placement
  let verticalPlacement: "above" | "below";
  if (preferredPlacement.vertical === "above") {
    // Prefer above, but check if there's enough space
    verticalPlacement = spaceAbove >= elementSize.height ? "above" : "below";
  } else {
    // Prefer below, but check if there's enough space
    verticalPlacement = spaceBelow >= elementSize.height ? "below" : "above";
  }

  // Calculate final position based on placement
  let x: number;
  let y: number;

  // Calculate X position
  if (horizontalPlacement === "left") {
    x = cursorPosition.x - elementSize.width - offset.x;
  } else {
    x = cursorPosition.x + offset.x;
  }

  // Calculate Y position
  if (verticalPlacement === "above") {
    y = cursorPosition.y - elementSize.height - offset.y;
  } else {
    y = cursorPosition.y + offset.y;
  }

  // Ensure the element doesn't go beyond viewport bounds
  x = Math.max(
    padding,
    Math.min(x, viewport.width - elementSize.width - padding),
  );
  y = Math.max(
    padding,
    Math.min(y, viewport.height - elementSize.height - padding),
  );

  return {
    x,
    y,
    placement: {
      horizontal: horizontalPlacement,
      vertical: verticalPlacement,
    },
  };
}

/**
 * Measure an element's dimensions by temporarily rendering it
 * Used for getting dropdown size before positioning
 */
export function measureElement(
  element: HTMLElement,
  options: {
    maxWidth?: number;
    maxHeight?: number;
  } = {},
): { width: number; height: number } {
  // Clone the element to measure it without affecting the original
  const clone = element.cloneNode(true) as HTMLElement;

  // Apply measurement styles
  clone.style.position = "absolute";
  clone.style.top = "-9999px";
  clone.style.left = "-9999px";
  clone.style.visibility = "hidden";
  clone.style.pointerEvents = "none";
  clone.style.zIndex = "-1";

  // Apply size constraints if provided
  if (options.maxWidth) {
    clone.style.maxWidth = `${options.maxWidth}px`;
  }
  if (options.maxHeight) {
    clone.style.maxHeight = `${options.maxHeight}px`;
  }

  // Add to DOM temporarily
  document.body.appendChild(clone);

  // Get dimensions
  const rect = clone.getBoundingClientRect();
  const dimensions = {
    width: rect.width,
    height: rect.height,
  };

  // Clean up
  document.body.removeChild(clone);

  return dimensions;
}

/**
 * Get cursor position within a textarea relative to viewport
 */
export function getCursorPositionInTextarea(
  textarea: HTMLTextAreaElement,
  cursorIndex: number,
): { x: number; y: number } {
  // Create a temporary div to measure text
  const dummy = document.createElement("div");
  const textareaStyles = window.getComputedStyle(textarea);

  // Copy textarea styles to dummy
  dummy.style.position = "absolute";
  dummy.style.top = "-9999px";
  dummy.style.left = "-9999px";
  dummy.style.whiteSpace = "pre-wrap";
  dummy.style.wordWrap = "break-word";
  dummy.style.font = textareaStyles.font;
  dummy.style.fontSize = textareaStyles.fontSize;
  dummy.style.fontFamily = textareaStyles.fontFamily;
  dummy.style.fontWeight = textareaStyles.fontWeight;
  dummy.style.lineHeight = textareaStyles.lineHeight;
  dummy.style.letterSpacing = textareaStyles.letterSpacing;
  dummy.style.width = `${textarea.clientWidth}px`;
  dummy.style.padding = textareaStyles.padding;
  dummy.style.border = textareaStyles.border;
  dummy.style.boxSizing = textareaStyles.boxSizing;

  document.body.appendChild(dummy);

  // Get text up to cursor position
  const textBeforeCursor = textarea.value.slice(0, cursorIndex);
  dummy.textContent = textBeforeCursor;

  // Create a span for the cursor position
  const cursorSpan = document.createElement("span");
  cursorSpan.textContent = "|"; // Cursor placeholder
  dummy.appendChild(cursorSpan);

  // Get cursor position relative to dummy
  const dummyRect = dummy.getBoundingClientRect();
  const cursorRect = cursorSpan.getBoundingClientRect();

  // Get textarea position
  const textareaRect = textarea.getBoundingClientRect();

  // Calculate cursor position relative to viewport
  const x = textareaRect.left + (cursorRect.left - dummyRect.left);
  const y = textareaRect.top + (cursorRect.top - dummyRect.top);

  // Clean up
  document.body.removeChild(dummy);

  return { x, y };
}
